import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { eventConfig } from "@/config/eventConfig";
import { Countdown } from "@/components/Countdown";

const NAV = [
  { label: "Watch", href: "#watch", id: "watch" },
  { label: "CPF Explorer", href: "#cpf-clarity", id: "cpf-clarity" },
  { label: "Stories", href: "#past-events", id: "past-events" },
  { label: "Agenda", href: "#agenda", id: "agenda" },
  { label: "Speakers", href: "#speakers", id: "speakers" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

const Header = ({ onRegister }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 620);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: "rgba(7,21,36,0.92)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.10)",
        transform: scrolled ? "translateY(0)" : "translateY(-100%)",
        opacity: scrolled ? 1 : 0,
        pointerEvents: scrolled ? "auto" : "none",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <a href="#top" className="flex items-center gap-3" data-testid="header-logo" aria-label="Affinity Group home">
            <img
              src={eventConfig.brand.logo}
              alt="Affinity Group logo"
              className="h-12 lg:h-14 w-auto"
              width="140"
              height="36"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                onClick={() => go(n.href)}
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Countdown variant="compact" className="hidden lg:inline-flex" />
            <button
              data-testid="header-register-button"
              onClick={onRegister}
              className="hidden sm:inline-flex items-center h-11 px-5 rounded-xl bg-[#F7B838] text-[#071524] text-sm font-semibold hover:bg-[#FFD16F] transition-colors active:scale-[0.98]"
            >
              Reserve a Seat
            </button>
            <button
              data-testid="header-mobile-menu-button"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl border border-white/15 bg-white/5 text-white"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#071524]/95 backdrop-blur-md">
          <div className="px-4 py-3 flex flex-col">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-link-${n.id}`}
                onClick={() => go(n.href)}
                className="text-left py-3 text-white/85 font-medium border-b border-white/5"
              >
                {n.label}
              </button>
            ))}
            <button
              data-testid="mobile-nav-register"
              onClick={() => { setMenuOpen(false); onRegister && onRegister(); }}
              className="mt-3 inline-flex items-center justify-center h-12 rounded-xl bg-[#F7B838] text-[#071524] text-sm font-semibold hover:bg-[#FFD16F] transition-colors"
            >
              Reserve a Seat
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
